import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useNavigate, useParams } from 'react-router';
import { useDialogs } from '../../hooks/useDialogs/useDialogs';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { GET_CUSTOMER_BY_ID_QUERY, GET_CUSTOMERS_QUERY } from '../../queries/CustomerQueries';
import { DELETE_CUSTOMER_BY_ID_MUTATION } from '../../mutations/CustomerMutations';
import type { Customer } from '../../models/Customer';
import { Accordion, AccordionDetails, AccordionSummary, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { AttachMoneyOutlined } from '@mui/icons-material';
import { InventoryOutlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { Product } from '../../models/Product';

export default function CustomerShow() {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [getCustomeryById] = useLazyQuery(
    GET_CUSTOMER_BY_ID_QUERY);

  const [deleteCustomerById] = useMutation(
    DELETE_CUSTOMER_BY_ID_MUTATION, {
    refetchQueries: [{ query: GET_CUSTOMERS_QUERY }],
  });

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(customerId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getCustomerByIdError } = await getCustomeryById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getCustomerByIdError) {
          setError(getCustomerByIdError);
        }

        if (data?.customer) {
          setCustomer(data.customer);
        } else {
          setError({ message: 'No customer found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getCustomeryById, customerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = useCallback(() => {
    navigate(`/dashboard/customers/${customerId}/edit`);
  }, [navigate, customerId]);

  const handleDelete = useCallback(async () => {
    if (!customer) {
      return;
    }

    const confirmed = await dialogs.confirm(
      `Do you wish to delete ${customer?.name}?`,
      {
        title: `Delete customer?`,
        severity: 'error',
        okText: 'Delete',
        cancelText: 'Cancel',
      },
    );

    if (confirmed) {
      setIsLoading(true);
      try {
        const { data: result } = await deleteCustomerById({ variables: { id: customer.id } });

        if (result?.deleted) {
          navigate('/dashboard/customers');

          notifications.show('Customer deleted successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });
        } else {
          notifications.show(
            'Failed to delete customer. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (deleteError) {
        notifications.show(
          `Failed to delete customer. Reason:' ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
      setIsLoading(false);
    }
  }, [customer, dialogs, deleteCustomerById, navigate, notifications]);

  const handleBack = useCallback(() => {
    navigate('/dashboard/customers');
  }, [navigate]);

  const renderShow = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return customer ? (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="customer-details-panel"
            id="customer-details-panel"
          >
            <Typography component="span" sx={{ fontSize: '16px' }}>
              <strong>Details</strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2} sx={{ width: '100%' }}>
              <Grid size={{ xs: 10, sm: 4 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Name</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {customer.name}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 10, sm: 4 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Address</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {customer.address}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 10, sm: 4 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">City</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {customer.city}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 10, sm: 4 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">State</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {customer.state}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 10, sm: 4 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Zip</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {customer.zip}
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 10, sm: 4 }}>
                <Paper sx={{ px: 2, py: 1 }}>
                  <Typography variant="overline">Contact</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {customer.contactId > 0 ?
                      <a href={`/dashboard/contacts/${customer.contact?.id}`}>{customer.contact?.name}</a> :
                      'N/A'
                    }
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="completed-batches-panel"
            id="completed-batches-panel"
          >
            <Typography component="span" sx={{ fontSize: '16px' }}>
              <strong>Products</strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer sx={{ maxHeight: '500px' }}>
              <Table sx={{ minWidth: 600 }} aria-label="dense table" size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '100%' }} component="th" scope="row">Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(customer.products || []).map((product: Product) => (
                    <TableRow
                      key={product.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '100%' }}>
                        <a href={`/dashboard/products/${product?.id}`}>{product?.name}</a>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="actions-panel"
            id="actions-panel"
          >
            <Typography component="span" sx={{ fontSize: '16px' }}>
              <strong>Actions</strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back
              </Button>
              <Stack direction="row" spacing={2}>
                {customer?.bulkPricingProducts && customer.bulkPricingProducts.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<AttachMoneyOutlined />}
                    component={Link}
                    to={`/dashboard/customers/${customer?.id}/bulk-costing`}
                  >
                    Bulk Costing
                  </Button>
                )}
                {customer?.products && customer.products.some(product => (product.productInventoryItems || []).length > 0) && (
                  <Button
                    variant="contained"
                    startIcon={<InventoryOutlined />}
                    component={Link}
                    to={`/dashboard/customers/${customer?.id}/inventory`}
                  >
                    Inventory
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box >
    ) : null;
  }, [isLoading, error, customer, handleBack, handleEdit, handleDelete]);

  const pageTitle = customer?.name || 'N/A';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Customers', path: '/dashboard/customers' },
        { title: pageTitle },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}
